import { useState, Fragment, FocusEventHandler, FocusEvent } from 'react';
const ValidationError = ({ copy }: { copy: string}) => {
    return <div className="alert-message">{copy}</div>;
};

interface InputValidatedOnBlurProps {
    // Text to show in the error-message when value not accepted
    errorCopy: string;
    // Function called to decide if value is accepted
    validator: (s: string) => boolean;
    // The value to be passed into validator as an argument. Forwarded to the `<input/>`.
    value: string;
    // CSS class name. Forwarded to the `<input/>`, potentially with some munging.
    className: string;
    // On-blur event handler. Forwarded to the `<input/>`, but wrapped in some extra behavior.
    onBlur?: FocusEventHandler;
    // All other props will be forwarded to an `<input />`.
    [x: string]: unknown;
}
/**
 * An `<input/>`, conditionally followed by a div with an error message. We'll show your error message if 1. the input's
 * value doesn't pass the provided validator and 2. a blur event has fired on this input (suggesting the user has
 * finished editing the input and is trying to move on).
 *
 * TODO Should this be renamed to something like InputValidatedOnBlur?
 */
export const InputValidatedOnBlur = ({ errorCopy, validator, value, className, onBlur: onBlurProp, ...otherProps }: InputValidatedOnBlurProps) => {
    const [hasBeenBlurred, setHasBeenBlurred] = useState(false);

    const isValid = validator(value) || !hasBeenBlurred;
    let errorMessage, extraClass;
    if (isValid) {
        errorMessage = null;
        extraClass = '';
    } else {
        errorMessage = <ValidationError copy={errorCopy} />;
        extraClass = 'hasError';
    }

    const onBlur = (e: FocusEvent<Element>) => {
        setHasBeenBlurred(true);

        if (onBlurProp) {
            onBlurProp(e);
        }
    };

    return (
        <Fragment>
            <input
                value={value}
                className={`${className} ${extraClass}`}
                onBlur={onBlur}
                {...otherProps}
            />
            {errorMessage}
        </Fragment>
    );
};
